/**
* @file: LeaderboardComponent
* This component is responsible for displaying the leaderboard of drivers.
* It fetches the leaderboard data from the server and displays it in a table format.
* It also provides a search functionality to filter the drivers based on their names.
* It uses Angular Material for the table and other UI components.
*/

import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { LeaderboardService } from '../../core/services/leaderboard/leaderboard.service';
import { Title } from '@angular/platform-browser';
import { FunnelGuardSyncService } from '../../core/services/driver/funnel-guard-sync.service';
import { LeaderboardDuration, LeaderboardDurationLabel } from '../../shared/constants/leaderboard-duration.constant';

export interface DriverData {
    name: string;
    rank: number;
    points: number;
    market_name: string;
}

/**
 * Leaderboard Component
 * Displays the leaderboard of drivers.
 * Allows filtering by driver name and selecting duration (monthly, yearly, all-time).
 * Fetches data from LeaderboardService and displays it in a table.
 * Hides header when displayed.
 * 
 */
@Component({
    standalone: false,
    selector: 'octo-leaderboard',
    templateUrl: './leaderboard.component.html',
    styleUrls: ['./leaderboard.component.scss']
})
export class LeaderboardComponent implements OnInit {

    isLoadingDrivers: boolean = true;
    lastUpdatedTime: string = '';

    durations: string[] = [LeaderboardDurationLabel.Monthly, LeaderboardDurationLabel.Yearly, LeaderboardDurationLabel.AllTime];
    selectedDuration: string = LeaderboardDurationLabel.Monthly;
    selectedDurationValue: string = LeaderboardDuration.Monthly;
    searchTerm: string = '';

    displayedColumns: string[] = ['rank', 'name', 'points'];
    dataSource: MatTableDataSource<DriverData> = new MatTableDataSource<DriverData>([]);

    /**
     * Constructor
     *
     * @param apiService {LeaderboardService} - The service to fetch leaderboard data
     * @param titleService {Title} - The service to set the page title
     * @param funnelGuardSyncService {FunnelGuardSyncService} - The service to manage funnel guard sync state
     */
    constructor(
        private readonly apiService: LeaderboardService,
        private readonly titleService: Title,
        private readonly funnelGuardSyncService: FunnelGuardSyncService
    ) {
        this.titleService.setTitle('Octopus Driver Leaderboard');
    }

    /**
     * OnInit lifecycle hook
     * Fetches the leaderboard data when the component is initialized
     * Sets the page title to "Octopus Driver Leaderboard"
     */
    ngOnInit(): void {
        this.getLeaderboard();
    }

    /**
     * Filters the drivers based on the search term
     * 
     */
    filterDrivers(): void {
        // Filter the data based on the search term
        this.dataSource.filter = this.searchTerm.trim().toLowerCase();
    }

    /**
     * Fetches the leaderboard data from the service
     * Updates the dataSource with the fetched data
     * Manages loading state and error handling
     * 
     */
    getLeaderboard(): void {
        this.isLoadingDrivers = true;

        // Fetch the leaderboard data from the service
        this.apiService.getLeaderboardDrivers(this.selectedDurationValue).then((drivers: any) => {

            this.dataSource.data = drivers.top_drivers || [];
            this.isLoadingDrivers = false;
            this.lastUpdatedTime = drivers.last_updated || '';

            this.funnelGuardSyncService.setFunnelGuardSync(true);

        }).catch((error: any) => {
            console.error('Error fetching leaderboard data:', error);
            this.isLoadingDrivers = false;
            this.dataSource.data = [];

            this.funnelGuardSyncService.setFunnelGuardSync(true);
        });
    }

    /**
     * Handles duration change event
     * Updates the selected duration and fetches the leaderboard data accordingly
     * 
     * @param duration {string} - The selected duration
     */
    onDurationChange(duration: string): void {

        switch (duration) {
            case LeaderboardDurationLabel.Monthly:
                this.selectedDurationValue = LeaderboardDuration.Monthly;
                this.selectedDuration = LeaderboardDurationLabel.Monthly;
                break;
            case LeaderboardDurationLabel.Yearly:
                this.selectedDurationValue = LeaderboardDuration.Yearly;
                this.selectedDuration = LeaderboardDurationLabel.Yearly;
                break;
            case LeaderboardDurationLabel.AllTime:
                this.selectedDurationValue = LeaderboardDuration.AllTime;
                this.selectedDuration = LeaderboardDurationLabel.AllTime;
                break;
            default:
                this.selectedDurationValue = LeaderboardDuration.Monthly;
                this.selectedDuration = LeaderboardDurationLabel.Monthly;

        }

        this.getLeaderboard();
    }
}
